/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
// Global
import { useMemo } from 'react';
import { tv } from 'tailwind-variants';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { useCardListContext } from './CardList';
import { getTestProps } from 'lib/testing/utils';
import useIsEditing from 'lib/hooks/useIsEditing';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
import { findComponent } from 'lib/utils/object-utils';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
const TestimonialCardItem = (props)=>{
    const { image, quote, authorName, authorTitle, description, cardLink1, cardLink2 } = props?.fields || {};
    const { RenderingIdentifier, smallContainer } = props?.params || {};
    const isSmallContainer = smallContainer === '1';
    const isEditing = useIsEditing();
    const { sitecoreContext } = useSitecoreContext();
    const { testimonialTabItemAuthorName, testimonialTabItemAuthorTitle } = useMemo(()=>{
        let name = '';
        let title = '';
        const testimonialTabItems = findComponent(sitecoreContext ?? {}, 'TestimonialTabItem');
        for (const testimonialTabItem of testimonialTabItems){
            const dynamicPlaceholderId = testimonialTabItem?.params?.DynamicPlaceholderId;
            const firstChildInPlaceholder = testimonialTabItem?.placeholders?.[`testimonialcard-${dynamicPlaceholderId}`]?.[0];
            if (props?.rendering?.uid === firstChildInPlaceholder?.uid) {
                name = testimonialTabItem?.fields?.authorName?.value || '';
                title = testimonialTabItem?.fields?.authorTitle?.value || '';
                break;
            }
        }
        return {
            testimonialTabItemAuthorName: name,
            testimonialTabItemAuthorTitle: title
        };
    }, [
        sitecoreContext,
        props?.rendering?.uid
    ]);
    const styles = parseStyleParams(props.params, [
        'cta1',
        'cta2'
    ]);
    // Get column count from CardList context
    const cardListColCount = useCardListContext();
    const { container, header, body, contentWrapper, content, quoteWrapper, quoteText, authorInfo, authorNameTV, authorTitleTV, divider, descriptionTV, footer, cta, imageWrapper } = TAILWIND_VARIANTS({
        colCount: cardListColCount,
        isSmallContainer
    });
    return /*#__PURE__*/ _jsxs("article", {
        className: container(),
        "data-component": "authorable/shared/lists/testimonialcarditem",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`testimonial-card-item-${props?.rendering?.uid}`),
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: header(),
                children: isEditing ? /*#__PURE__*/ _jsx(ImageWrapper, {
                    field: image,
                    className: imageWrapper(),
                    ...getTestProps(`image`)
                }) : /*#__PURE__*/ _jsx(_Fragment, {
                    children: image?.value?.src ? /*#__PURE__*/ _jsx(ImageWrapper, {
                        field: image,
                        className: imageWrapper(),
                        ...getTestProps(`image`)
                    }) : /*#__PURE__*/ _jsx(ImageWrapper, {
                        field: {
                            value: {
                                src: '/assets/profile_placeholder.png',
                                alt: 'No image'
                            }
                        },
                        className: imageWrapper(),
                        ...getTestProps(`related-profile-image-placeholder`)
                    })
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: body(),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: contentWrapper(),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: content(),
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: quoteWrapper(),
                                    children: /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                        className: quoteText(),
                                        field: quote,
                                        ...getTestProps(`quote`)
                                    })
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: authorInfo(),
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                className: authorNameTV(),
                                                field: authorName,
                                                tag: "h3",
                                                ...getTestProps(`author-name`)
                                            }),
                                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                                className: authorTitleTV(),
                                                field: authorTitle,
                                                tag: "p",
                                                ...getTestProps(`author-title`)
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: divider()
                                }),
                                /*#__PURE__*/ _jsx(RichTextWrapper, {
                                    className: descriptionTV(),
                                    field: description,
                                    ...getTestProps(`description`)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: footer(),
                            children: [
                                /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "component-featureCard-button-1-color",
                                    className: cta(),
                                    ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                                    field: cardLink1,
                                    ctaIcon: "arrow-right",
                                    ...getTestProps(`card-link-1`),
                                    gtmEvent: createCTAClickGTMEvent({
                                        header: `${testimonialTabItemAuthorName} ${testimonialTabItemAuthorTitle}`,
                                        ctaText: cardLink1?.value?.text,
                                        ctaType: 'blue',
                                        componentName: 'Testimonials',
                                        ctaLink: cardLink1?.value?.href
                                    })
                                }),
                                /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaComponentClass: "component-featureCard-button-2-color",
                                    className: cta(),
                                    ctaVariant: "secondaryInverse",
                                    field: cardLink2,
                                    ctaIcon: "arrow-right",
                                    ...getTestProps(`card-link-2`),
                                    gtmEvent: createCTAClickGTMEvent({
                                        header: `${testimonialTabItemAuthorName} ${testimonialTabItemAuthorTitle}`,
                                        ctaText: cardLink2?.value?.text,
                                        ctaType: 'clear',
                                        componentName: 'Testimonials',
                                        ctaLink: cardLink2?.value?.href
                                    })
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
};
export const Default = withStandardComponentWrapper(TestimonialCardItem);
const TAILWIND_VARIANTS = tv({
    slots: {
        container: [
            'flex',
            'w-full',
            'h-full',
            'overflow-hidden',
            'rounded-general-border-radius-card',
            'bg-component-featureCard-surface'
        ],
        header: [
            'relative',
            'bg-color-grayscale-400',
            'min-h-max',
            'w-full',
            'overflow-hidden',
            'aspect-[4/3]',
            'text-color-fill-brand-1',
            'transition',
            'duration-300',
            'ease-in-out',
            'group'
        ],
        body: [
            'flex',
            'flex-col',
            'w-full',
            'items-center'
        ],
        contentWrapper: [
            'flex',
            'flex-col',
            'w-full',
            'gap-spacing-spacing-4',
            'py-component-featureCard-padding-top',
            'px-component-featureCard-padding-x',
            'pb-component-featureCard-padding-bottom'
        ],
        content: [
            'flex',
            'flex-col',
            'w-full',
            'gap-spacing-spacing-24'
        ],
        quoteWrapper: [],
        quoteText: [
            'text-component-featureCard-title',
            'font-typography-font-family-header',
            'text-typography-font-size-header-small',
            'leading-typography-line-height-small',
            'lg:leading-typography-line-height-medium',
            'lg:text-typography-font-size-header-medium',
            'italic',
            'md:not-italic',
            'leading-typography-line-height-header-large',
            'relative',
            'before:content-["""]',
            'before:absolute',
            'before:-left-4',
            'before:-top-2',
            'before:text-4xl',
            'before:text-component-featureCard-title',
            'before:opacity-30',
            'after:content-["""]',
            'after:absolute',
            'after:-bottom-4',
            'after:-right-4',
            'after:text-4xl',
            'after:text-component-featureCard-title',
            'after:opacity-30'
        ],
        authorInfo: [
            'flex',
            'items-center',
            'gap-spacing-spacing-4',
            'mb-spacing-spacing-6'
        ],
        authorNameTV: [
            'font-semibold',
            'font-typography-font-family-body',
            'text-typography-font-size-body-medium',
            'text-component-featureCard-title',
            'leading-normal'
        ],
        authorTitleTV: [
            'text-typography-font-size-body-medium',
            'font-typography-font-family-body',
            'text-component-featureCard-subtitle',
            'leading-normal',
            'font-typography-font-weight-body-semibold'
        ],
        descriptionTV: [
            'text-typography-font-size-body-medium',
            'text-component-featureCard-body',
            'font-typography-font-weight-body',
            'leading-normal'
        ],
        footer: [
            'flex',
            'flex-wrap',
            'gap-2',
            'items-left',
            'justify-left',
            'w-fit',
            'flex-col',
            'items-baseline',
            'mt-component-featureCard-button-margin-top'
        ],
        cta: [
            'flex',
            'gap-spacing-spacing-8',
            'items-center',
            'justify-start',
            'py-spacing-spacing-12'
        ],
        imageWrapper: [
            'w-full',
            'h-full',
            'object-cover'
        ],
        divider: [
            'w-component-section-divider-width',
            'h-component-section-divider-height',
            'bg-component-section-divider'
        ]
    },
    variants: {
        colCount: {
            '1': {
                container: [
                    'flex-col',
                    'md:flex-row',
                    'md:gap-0'
                ],
                body: [
                    'flex-row',
                    'items-start',
                    'text-left',
                    'lg:pl-0',
                    'justify-center'
                ],
                content: [
                    'text-left',
                    'justify-center'
                ],
                contentWrapper: [
                    'text-left',
                    'justify-center'
                ]
            },
            '2': {
                container: [
                    'flex-col',
                    'md:flex-row'
                ],
                body: [
                    'flex-row',
                    'items-start',
                    'text-left',
                    'justify-center'
                ],
                content: [
                    'text-left'
                ],
                contentWrapper: [
                    'text-left',
                    'justify-center',
                    'sm:py-component-featureCard-padding-top',
                    'sm:px-component-featureCard-padding-x',
                    'sm:pb-component-featureCard-padding-bottom'
                ],
                header: [
                    'aspect-[4/3]',
                    'sm:py-component-featureCard-padding-top',
                    'sm:px-component-featureCard-padding-x'
                ]
            },
            '3': {
                container: [
                    'flex-col'
                ],
                body: [
                    'flex-col',
                    'items-start',
                    'text-left'
                ],
                content: [
                    'text-left'
                ],
                contentWrapper: [
                    'h-full',
                    'text-left'
                ],
                footer: [
                    'mt-auto'
                ]
            },
            '4': {
                container: [
                    'flex-col'
                ],
                body: [
                    'flex-col',
                    'items-center',
                    'text-left'
                ],
                content: [
                    'text-left'
                ],
                contentWrapper: [
                    'h-full',
                    'text-left'
                ],
                footer: [
                    'mt-auto'
                ]
            }
        },
        isSmallContainer: {
            true: {
                container: [
                    'contentWrapper'
                ]
            },
            false: {
                container: [
                    ''
                ]
            }
        }
    },
    defaultVariants: {
        colCount: '1',
        isSmallContainer: false
    }
});
